import React from "react";
import EtiquetaSheet from "./EtiquetaSheet";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const COLUMNS = 5;
const ROWS = 13;
const TOTAL_LABELS = COLUMNS * ROWS;
const MAX_LABEL_GROUPS = 4;

const clampText = (value, maxChars) => {
  if (!value) return "";
  const clean = String(value).trim();
  if (clean.length <= maxChars) return clean;
  return `${clean.slice(0, maxChars - 1)}…`;
};

const formatDatePtBr = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("pt-BR");
};

const INITIAL_ENTRY = {
  name: "",
  fabrication: "",
  validity: "",
  quantity: "",
  hasQrCode: false,
  qrCodeText: "",
  customText: "",
  useFabricationDate: true,
  fabricationText: "",
  useValidityDate: true,
  validityText: "",
};

function EtiqueLabelGenerator() {
  const [entries, setEntries] = React.useState(() =>
    Array.from({ length: MAX_LABEL_GROUPS }, () => ({ ...INITIAL_ENTRY }))
  );

  const handleEntryChange = React.useCallback((index, field, value) => {
    setEntries((prev) =>
      prev.map((entry, idx) => {
        if (idx !== index) return entry;

        // Handle quantity field (only numbers)
        if (field === "quantity") {
          return { ...entry, [field]: value.replace(/[^0-9]/g, "") };
        }

        // Handle hasQrCode toggle
        if (field === "hasQrCode") {
          return {
            ...entry,
            hasQrCode: value,
            // Clear the opposite field when toggling
            qrCodeText: value ? entry.qrCodeText : "",
            customText: value ? "" : entry.customText,
          };
        }

        // Handle useFabricationDate toggle
        if (field === "useFabricationDate") {
          return {
            ...entry,
            useFabricationDate: value,
            // Clear the opposite field when toggling
            fabrication: value ? entry.fabrication : "",
            fabricationText: value ? "" : entry.fabricationText,
          };
        }

        // Handle useValidityDate toggle
        if (field === "useValidityDate") {
          return {
            ...entry,
            useValidityDate: value,
            // Clear the opposite field when toggling
            validity: value ? entry.validity : "",
            validityText: value ? "" : entry.validityText,
          };
        }

        return { ...entry, [field]: value };
      })
    );
  }, []);

  const { labels, filledCount, totalRequested } = React.useMemo(() => {
    let remaining = TOTAL_LABELS;
    let used = 0;
    const result = [];

    const parseQuantity = (value) => {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return 0;
      return Math.max(0, Math.floor(parsed));
    };

    entries.forEach((entry) => {
      const quantity = parseQuantity(entry.quantity);
      const trimmedName = entry.name.trim();
      if (!trimmedName || quantity === 0 || remaining === 0) {
        return;
      }

      const fabricationLabel = formatDatePtBr(entry.fabrication);
      const validityLabel = formatDatePtBr(entry.validity);
      const labelTemplate = {
        name: clampText(trimmedName, 32),
        fabrication: entry.useFabricationDate
          ? clampText(fabricationLabel, 12)
          : clampText(entry.fabricationText || "", 50),
        validade: entry.useValidityDate
          ? clampText(validityLabel, 12)
          : clampText(entry.validityText || "", 50),
        useFabricationDate: entry.useFabricationDate,
        useValidityDate: entry.useValidityDate,
        qr: entry.hasQrCode ? entry.qrCodeText || null : null,
        customText: !entry.hasQrCode ? entry.customText || null : null,
      };

      const toAdd = Math.min(quantity, remaining);
      for (let i = 0; i < toAdd; i += 1) {
        result.push(labelTemplate);
      }
      used += toAdd;
      remaining -= toAdd;
    });

    const totalQtyRequested = entries.reduce(
      (sum, entry) =>
        sum + Math.max(0, Math.floor(Number(entry.quantity) || 0)),
      0
    );

    while (result.length < TOTAL_LABELS) {
      result.push(null);
    }

    return {
      labels: result.slice(0, TOTAL_LABELS),
      filledCount: used,
      totalRequested: totalQtyRequested,
    };
  }, [entries]);

  const remainingCount = TOTAL_LABELS - filledCount;
  const overflow = totalRequested > TOTAL_LABELS;

  // PDF
  const handleGeneratePdf = async () => {
    const { Document, Page, Text, View, StyleSheet, Image, pdf } = await import(
      "@react-pdf/renderer"
    );
    const QRCode = await import("qrcode");
    const styles = StyleSheet.create({
      page: {
        flexDirection: "row",
        flexWrap: "wrap",
        padding: "20px 0px 6px 4px",
        backgroundColor: "#fff",
        width: "100%",
        height: "100%",
      },
      grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        width: "100%",
        justifyContent: "flex-start",
      },
      label: {
        width: 111,
        minWidth: 111,
        height: 62,
        marginRight: 9,
        padding: "0 0 0 6px",
        flexDirection: "row",
        alignItems: "center",
        fontSize: 10,
        backgroundColor: "#fff",
      },
      labelLastInRow: {
        marginRight: 0,
      },
      qr: {
        marginRight: 6,
        marginLeft: 0,
        width: 62,
        height: 36,
      },
      customTextBox: {
        marginRight: 6,
        marginLeft: 0,
        width: 62,
        height: 36,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 6,
        fontWeight: "bold",
        color: "#374151",
        textAlign: "center",
        padding: 2,
      },
      info: {
        flexDirection: "column",
        alignItems: "flex-start",
        flexShrink: 1,
        width: "100%",
      },
      name: {
        fontWeight: "bold",
        fontSize: 10,
        width: "100%",
        marginBottom: 2,
        lineHeight: 1.1,
      },
      small: {
        fontSize: 7,
        lineHeight: 1.1,
      },
    });

    // Gera SVGs dos QRCodes
    const qrSvgs = await Promise.all(
      labels.map(async (label) => {
        if (label?.qr) {
          return await QRCode.toDataURL(label.qr, { width: 24, margin: 0 });
        }
        return null;
      })
    );

    const MyDocument = (
      <Document>
        <Page size="A4" style={styles.page}>
          <View style={styles.grid}>
            {labels.map((label, idx) => {
              const isRowEnd = (idx + 1) % COLUMNS === 0;
              if (!label) {
                return (
                  <View
                    key={`empty-${idx}`}
                    style={[styles.label, isRowEnd && styles.labelLastInRow]}
                  />
                );
              }
              return (
                <View
                  key={`label-${idx}`}
                  style={[styles.label, isRowEnd && styles.labelLastInRow]}
                >
                  <View style={styles.info}>
                    <Text style={styles.name} wrap>
                      {label.name}
                    </Text>
                    <Text style={styles.small} wrap>
                      {label.useFabricationDate ? "Fab: " : ""}
                      {label.fabrication || ""}
                    </Text>
                    <Text style={styles.small} wrap>
                      {label.useValidityDate ? "Val: " : ""}
                      {label.validade || ""}
                    </Text>
                  </View>
                  {qrSvgs[idx] ? (
                    <Image style={styles.qr} src={qrSvgs[idx]} />
                  ) : label.customText ? (
                    <View style={styles.customTextBox}>
                      <Text wrap>{label.customText}</Text>
                    </View>
                  ) : null}
                </View>
              );
            })}
          </View>
        </Page>
      </Document>
    );
    const blob = await pdf(MyDocument).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  return (
    <div className="no-print w-full max-w-3xl mx-auto px-2 py-6">
      <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 mb-6 flex flex-col gap-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center">
          Gerador de Etiquetas Adesivas (65 /100 folhas A4)
        </h2>
        <p className="text-sm text-gray-600 text-center">
          Etiquetas geradas: {filledCount}/{TOTAL_LABELS}. Restantes em branco:{" "}
          {remainingCount}.
        </p>
        {overflow && (
          <p className="text-xs text-amber-600 text-center">
            O total informado excede {TOTAL_LABELS}. Apenas as primeiras{" "}
            {TOTAL_LABELS} etiquetas serão utilizadas.
          </p>
        )}
        <form className="space-y-4">
          <div className="grid gap-4">
            {entries.map((entry, index) => (
              <div
                key={`entry-${index}`}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-800">
                    Produto {index + 1}
                  </h3>
                  <span className="text-xs text-gray-500">
                    {Number(entry.quantity) || 0} etiquetas
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex flex-col">
                    <label
                      htmlFor={`product-name-${index}`}
                      className="font-semibold text-sm mb-1"
                    >
                      Nome do Produto
                    </label>
                    <input
                      id={`product-name-${index}`}
                      type="text"
                      placeholder="Digite o nome do produto"
                      className="w-full border border-gray-300 rounded-lg h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={entry.name}
                      onChange={(e) =>
                        handleEntryChange(index, "name", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex flex-col">
                    <label
                      htmlFor={`quantity-${index}`}
                      className="font-semibold text-sm mb-1"
                    >
                      Quantidade de Etiquetas
                    </label>
                    <input
                      id={`quantity-${index}`}
                      type="number"
                      min={0}
                      className="w-full border border-gray-300 rounded-lg h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={entry.quantity}
                      onChange={(e) =>
                        handleEntryChange(index, "quantity", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center space-x-2 mb-2">
                      <Switch
                        id={`fab-date-switch-${index}`}
                        checked={entry.useFabricationDate}
                        onCheckedChange={(checked) =>
                          handleEntryChange(
                            index,
                            "useFabricationDate",
                            checked
                          )
                        }
                      />
                      <Label
                        htmlFor={`fab-date-switch-${index}`}
                        className="font-semibold text-sm cursor-pointer"
                      >
                        Usar Data de Fabricação
                      </Label>
                    </div>
                    {entry.useFabricationDate ? (
                      <input
                        id={`fabrication-${index}`}
                        type="date"
                        className="w-full border border-gray-300 rounded-lg h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={entry.fabrication}
                        onChange={(e) =>
                          handleEntryChange(
                            index,
                            "fabrication",
                            e.target.value
                          )
                        }
                      />
                    ) : (
                      <input
                        id={`fabrication-text-${index}`}
                        type="text"
                        placeholder="Ex: Recente, Lote 2024, etc."
                        className="w-full border border-gray-300 rounded-lg h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={entry.fabricationText}
                        onChange={(e) =>
                          handleEntryChange(
                            index,
                            "fabricationText",
                            e.target.value
                          )
                        }
                      />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center space-x-2 mb-2">
                      <Switch
                        id={`val-date-switch-${index}`}
                        checked={entry.useValidityDate}
                        onCheckedChange={(checked) =>
                          handleEntryChange(index, "useValidityDate", checked)
                        }
                      />
                      <Label
                        htmlFor={`val-date-switch-${index}`}
                        className="font-semibold text-sm cursor-pointer"
                      >
                        Usar Data de Validade
                      </Label>
                    </div>
                    {entry.useValidityDate ? (
                      <input
                        id={`validity-${index}`}
                        type="date"
                        className="w-full border border-gray-300 rounded-lg h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={entry.validity}
                        onChange={(e) =>
                          handleEntryChange(index, "validity", e.target.value)
                        }
                      />
                    ) : (
                      <input
                        id={`validity-text-${index}`}
                        type="text"
                        placeholder="Ex: 30 dias, Ver embalagem, etc."
                        className="w-full border border-gray-300 rounded-lg h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={entry.validityText}
                        onChange={(e) =>
                          handleEntryChange(
                            index,
                            "validityText",
                            e.target.value
                          )
                        }
                      />
                    )}
                  </div>
                </div>

                {/* QR Code Section */}
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <Switch
                      id={`qr-switch-${index}`}
                      checked={entry.hasQrCode}
                      onCheckedChange={(checked) =>
                        handleEntryChange(index, "hasQrCode", checked)
                      }
                    />
                    <Label
                      htmlFor={`qr-switch-${index}`}
                      className="font-semibold text-sm cursor-pointer"
                    >
                      Adicionar QR Code na etiqueta
                    </Label>
                  </div>

                  {entry.hasQrCode ? (
                    <div className="flex flex-col">
                      <label
                        htmlFor={`qr-text-${index}`}
                        className="font-semibold text-sm mb-1 text-blue-700"
                      >
                        Texto/URL para o QR Code
                      </label>
                      <input
                        id={`qr-text-${index}`}
                        type="text"
                        placeholder="Ex: https://wa.me/5511999999999 ou texto personalizado"
                        className="w-full border border-blue-300 rounded-lg h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={entry.qrCodeText}
                        onChange={(e) =>
                          handleEntryChange(index, "qrCodeText", e.target.value)
                        }
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Informe a URL ou texto que será codificado no QR Code
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <label
                        htmlFor={`custom-text-${index}`}
                        className="font-semibold text-sm mb-1 text-gray-700"
                      >
                        Texto personalizado (opcional)
                      </label>
                      <input
                        id={`custom-text-${index}`}
                        type="text"
                        placeholder="Ex: Promoção especial, Lote 001, etc."
                        className="w-full border border-gray-300 rounded-lg h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={entry.customText}
                        onChange={(e) =>
                          handleEntryChange(index, "customText", e.target.value)
                        }
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Campo opcional para adicionar informações extras na
                        etiqueta
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col md:flex-row gap-3 md:gap-6 justify-center items-center mt-2">
            <button
              type="button"
              onClick={handleGeneratePdf}
              className="w-full md:w-auto bg-green-700 hover:bg-green-800 text-white font-semibold px-6 py-2 rounded-lg shadow transition"
            >
              Visualizar Impressão
            </button>
          </div>
        </form>
      </div>
      <div className="w-full max-w-3xl mx-auto px-2 overflow-x-auto">
        <EtiquetaSheet labels={labels} />
      </div>
    </div>
  );
}

export default EtiqueLabelGenerator;
